import React from 'react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { AutocompleteComponents } from '@algolia/autocomplete-shared';
import getAttributeValue from '@white-nl/instantsearch-components/dist/helpers/getAttributeValue';
import { ArrowUpRight, MagnifyingGlass } from '../../ui/icons/classic/regular';

export interface AutocompleteItemTranslations {
  selectButtonText: string;
  fillQueryButtonText: string;
}

export const defaultTranslations: AutocompleteItemTranslations = {
  selectButtonText: 'Select',
  fillQueryButtonText: 'Fill query with',
};

export interface AutocompleteItemProps {
  hit: any;
  url: string;
  components: AutocompleteComponents;
  imageAttribute?: string | string[];
  imagePlaceholder?: React.ReactNode | null | undefined;
  titleAttribute?: string | string[];
  descriptionAttribute?: string | string[];
  translations?: AutocompleteItemTranslations;
  onFillQuery?: (hit: any) => void;
  children?: React.ReactNode | undefined;
  actions?: React.ReactNode[] | React.ReactNode | undefined;
}

export default function AutocompleteItem({
  hit,
  url,
  components,
  imageAttribute = undefined,
  imagePlaceholder = undefined,
  titleAttribute = 'title',
  descriptionAttribute = undefined,
  translations = undefined,
  onFillQuery = undefined,
  children = undefined,
  actions = undefined,
}: AutocompleteItemProps) {
  const t = { ...defaultTranslations, ...translations || {} };

  const imageUrl = getAttributeValue(hit, imageAttribute);
  let image = (imagePlaceholder !== undefined) ? (imagePlaceholder) : (
    <div className="aa-ItemIcon aa-ItemIcon--noBorder">
      <MagnifyingGlass />
    </div>
  );
  if (imageUrl) {
    image = (
      <div className="aa-ItemIcon aa-ItemIcon--picture aa-ItemIcon--alignTop">
        <img src={imageUrl} alt={getAttributeValue(hit, titleAttribute)} />
      </div>
    );
  }

  const selectButton = (
    t.selectButtonText && (
      <button
        className="aa-ItemActionButton aa-DesktopOnly aa-ActiveOnly aa-SelectIcon"
        type="button"
        title={t.selectButtonText}
        aria-label={t.selectButtonText}
        disabled
      >
        <ArrowUpRight />
      </button>
    )
  );

  const fillQueryButton = (
    onFillQuery && t.fillQueryButtonText && (
      <button
        className="aa-ItemActionButton"
        type="button"
        title={`${t.fillQueryButtonText} "${getAttributeValue(hit, titleAttribute)}"`}
        aria-label={`${t.fillQueryButtonText} "${getAttributeValue(hit, titleAttribute)}"`}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onFillQuery?.(hit);
        }}
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 17v-7.586l8.293 8.293c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414l-8.293-8.293h7.586c0.552 0 1-0.448 1-1s-0.448-1-1-1h-10c-0.552 0-1 0.448-1 1v10c0 0.552 0.448 1 1 1s1-0.448 1-1z" />
        </svg>
      </button>
    )
  );

  return (
    <a href={url} className="aa-ItemLink">
      <div className="aa-ItemContent">
        {image}
        <div className="aa-ItemContentBody">
          <div className="aa-ItemContentTitle">
            <components.Highlight hit={hit} attribute={titleAttribute} />
          </div>
          <div className="aa-ItemContentDescription">
            {getAttributeValue(hit, descriptionAttribute)}
          </div>
          {children}
        </div>
      </div>
      <div className="aa-ItemActions">
        {selectButton}
        {fillQueryButton}
        {actions}
      </div>
    </a>
  );
}
